import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import {
  adjustStock,
  moveBulk,
  moveUnits,
  productQuery,
  productsQuery,
  stockLocationsQuery,
  STATUS_LABELS,
  type ProductDetail,
} from '../api/inventory.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ INV-A12 at v1.955: where a product is kept — the depot and each venue that keeps stock —
// and moving it between them, at once, with why. INV-A02: correcting a bulk count, with why. Each
// refusal is the database's (a move that would leave a job short, more than is there) and is shown
// as the API says it.

type Mode = 'move' | 'adjust' | null;

export function StockByLocation({
  product,
  mayMove,
  mayAdjust,
}: {
  product: ProductDetail;
  mayMove: boolean;
  mayAdjust: boolean;
}) {
  const [mode, setMode] = useState<Mode>(null);
  const locations = product.stockByLocation;
  // With only a depot there is nowhere to move to, so the section says less.
  const venues = locations.filter((l) => l.location.kind === 'venue');
  // INV-A14 at v1.982: a venue's own equipment stays at that venue; it is counted, never moved.
  const venueOwned = product.ownerVenueId != null;
  // INV-A12 / INV-A14 at v1.986: your stock goes only where your stock is kept (`keepsStock`). A
  // location kept for a venue's own equipment can still be moved from. The database refuses the
  // rest; this saves the wasted try. Move stock waits for the list, so it never offers a refused place.
  const kept = useQuery(stockLocationsQuery);
  const destinations = kept.data
    ? locations.filter((l) => kept.data.find((k) => k.id === l.location.id)?.keepsStock)
    : [];
  const canMove = mayMove && venues.length > 0 && !venueOwned && destinations.length > 1;
  const canAdjust = mayAdjust && product.tracking === 'bulk';

  return (
    <section className="flex flex-col gap-4" aria-labelledby="where">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="where" className="section-title">
          Where it is
        </h2>
        {mode === null && (canMove || canAdjust) ? (
          <div className="flex gap-2">
            {canMove ? (
              <Button variant="quiet" onClick={() => setMode('move')}>
                Move
              </Button>
            ) : null}
            {canAdjust ? (
              <Button variant="quiet" onClick={() => setMode('adjust')}>
                Adjust stock
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {mode === 'move' ? (
        product.tracking === 'serialised' ? (
          <MoveUnitsForm
            product={product}
            destinations={destinations}
            onDone={() => setMode(null)}
          />
        ) : (
          <MoveBulkForm
            product={product}
            destinations={destinations}
            onDone={() => setMode(null)}
          />
        )
      ) : null}
      {mode === 'adjust' ? <AdjustForm product={product} onDone={() => setMode(null)} /> : null}
      {venueOwned ? (
        <p className="text-ink-soft">
          The venue’s own: kept at {locations[0]?.location.name ?? 'the venue'}, and never moves
          from there.
        </p>
      ) : null}
      {venues.length === 0 ? (
        <p className="text-ink-soft">
          All at the depot. Tick “stock is kept here” on a venue to keep some there.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="data-table">
            <caption className="sr-only">On hand at each location</caption>
            <thead>
              <tr>
                <th scope="col">Location</th>
                <th scope="col" className="text-right">
                  On hand
                </th>
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => (
                <tr key={l.location.id}>
                  <th scope="row" className="font-semibold">
                    {l.location.name}
                    {l.location.kind === 'venue' ? (
                      <span className="ml-2 text-xs font-normal text-muted">venue</span>
                    ) : null}
                  </th>
                  <td className="text-right tabular-nums">{l.onHand}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function useSaved(productId: string, onDone: () => void) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: productQuery(productId).queryKey });
    await queryClient.invalidateQueries({ queryKey: productsQuery.queryKey });
    await queryClient.invalidateQueries({ queryKey: stockLocationsQuery.queryKey });
    onDone();
  };
}

function LocationSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  options: ProductDetail['stockByLocation'];
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs text-ink-soft">{label}</span>
      <select className="input" value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        {options.map((l) => (
          <option key={l.location.id} value={l.location.id}>
            {l.location.name} ({l.onHand} on hand)
          </option>
        ))}
      </select>
    </label>
  );
}

function FormShell({
  title,
  onSubmit,
  error,
  pending,
  submitLabel,
  onCancel,
  children,
}: {
  title: string;
  onSubmit: (event: FormEvent) => void;
  error: Error | null;
  pending: boolean;
  submitLabel: string;
  onCancel: () => void;
  children: ReactNode;
}) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 border-2 border-divider p-4">
      <h3 className="font-bold">{title}</h3>
      {children}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={pending}>
          {submitLabel}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

type Destinations = ProductDetail['stockByLocation'];

function MoveBulkForm({
  product,
  destinations,
  onDone,
}: {
  product: ProductDetail;
  destinations: Destinations;
  onDone: () => void;
}) {
  const locations = product.stockByLocation;
  const firstWithStock = locations.find((l) => l.onHand > 0) ?? locations[0]!;
  const [from, setFrom] = useState(firstWithStock.location.id);
  const [to, setTo] = useState(
    (destinations.find((l) => l.location.id !== firstWithStock.location.id) ?? destinations[0]!)
      .location.id,
  );
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [askWhy, setAskWhy] = useState(false);
  const saved = useSaved(product.id, onDone);
  const move = useMutation({
    mutationFn: () =>
      moveBulk(product.id, { from, to, quantity: Number(quantity), reason: reason.trim() }),
    onSuccess: saved,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    // A reason of only spaces is no reason; say so here rather than as the API's field error.
    if (!reason.trim()) {
      setAskWhy(true);
      return;
    }
    move.mutate();
  }

  return (
    <FormShell
      title={`Move ${product.name}`}
      onSubmit={submit}
      error={move.error}
      pending={move.isPending}
      submitLabel="Move"
      onCancel={onDone}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <LocationSelect label="From" value={from} onChange={setFrom} options={locations} />
        <LocationSelect label="To" value={to} onChange={setTo} options={destinations} />
        <Field
          label="How many"
          type="number"
          inputMode="numeric"
          min={1}
          required
          value={quantity}
          onChange={(e) => setQuantity(e.currentTarget.value)}
        />
      </div>
      <Field
        label="Why"
        hint="Kept with the move, e.g. “Kept there for the season”."
        required
        value={reason}
        error={askWhy && !reason.trim() ? 'Say why.' : undefined}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
    </FormShell>
  );
}

function MoveUnitsForm({
  product,
  destinations,
  onDone,
}: {
  product: ProductDetail;
  destinations: Destinations;
  onDone: () => void;
}) {
  const locations = product.stockByLocation;
  // A unit on a job, in transit, lost or retired is not moved; the database refuses it anyway.
  const movable = product.assets.filter((a) =>
    ['in_stock', 'in_repair', 'quarantined'].includes(a.status),
  );
  const [to, setTo] = useState(
    (destinations.find((l) => l.location.kind === 'venue') ?? destinations[0]!).location.id,
  );
  const [chosen, setChosen] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [askWhy, setAskWhy] = useState(false);
  const saved = useSaved(product.id, onDone);
  const move = useMutation({
    mutationFn: () => moveUnits(product.id, { to, assetIds: chosen, reason: reason.trim() }),
    onSuccess: saved,
  });
  // Only units somewhere else are offered: moving a unit to where it is does nothing.
  const offered = movable.filter((a) => a.location.id !== to);

  function submit(event: FormEvent) {
    event.preventDefault();
    // A reason of only spaces is no reason; say so here rather than as the API's field error.
    if (!reason.trim()) {
      setAskWhy(true);
      return;
    }
    if (chosen.length === 0) return;
    move.mutate();
  }

  return (
    <FormShell
      title={`Move units of ${product.name}`}
      onSubmit={submit}
      error={move.error}
      pending={move.isPending}
      submitLabel={chosen.length > 1 ? `Move ${chosen.length} units` : 'Move'}
      onCancel={onDone}
    >
      <LocationSelect
        label="To"
        value={to}
        onChange={(id) => {
          setTo(id);
          setChosen([]);
        }}
        options={destinations}
      />
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-xs text-ink-soft">Which units</legend>
        {offered.length === 0 ? (
          <p className="text-sm text-muted">Every unit that can move is already there.</p>
        ) : (
          offered.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={chosen.includes(a.id)}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setChosen((c) => (on ? [...c, a.id] : c.filter((id) => id !== a.id)));
                }}
              />
              <span className="font-mono font-semibold">{a.assetTag ?? a.serialNumber}</span>
              <span className="text-muted">
                at {a.location.name}
                {a.status !== 'in_stock' ? ` · ${STATUS_LABELS[a.status]}` : ''}
              </span>
            </label>
          ))
        )}
      </fieldset>
      <Field
        label="Why"
        hint="Kept with the move."
        required
        value={reason}
        error={askWhy && !reason.trim() ? 'Say why.' : undefined}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
    </FormShell>
  );
}

function AdjustForm({ product, onDone }: { product: ProductDetail; onDone: () => void }) {
  const locations = product.stockByLocation;
  const [locationId, setLocationId] = useState(locations[0]!.location.id);
  const [direction, setDirection] = useState<'add' | 'remove'>('remove');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [askWhy, setAskWhy] = useState(false);
  const saved = useSaved(product.id, onDone);
  const adjust = useMutation({
    mutationFn: () =>
      adjustStock(product.id, {
        locationId,
        delta: (direction === 'add' ? 1 : -1) * Number(quantity),
        reason: reason.trim(),
      }),
    onSuccess: saved,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    // A reason of only spaces is no reason; say so here rather than as the API's field error.
    if (!reason.trim()) {
      setAskWhy(true);
      return;
    }
    adjust.mutate();
  }

  return (
    <FormShell
      title={`Correct the count of ${product.name}`}
      onSubmit={submit}
      error={adjust.error}
      pending={adjust.isPending}
      submitLabel="Save"
      onCancel={onDone}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <LocationSelect
          label="Where"
          value={locationId}
          onChange={setLocationId}
          options={locations}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Add or remove</span>
          <select
            className="input"
            value={direction}
            onChange={(e) => setDirection(e.currentTarget.value as 'add' | 'remove')}
          >
            <option value="remove">Remove</option>
            <option value="add">Add</option>
          </select>
        </label>
        <Field
          label="How many"
          type="number"
          inputMode="numeric"
          min={1}
          required
          value={quantity}
          onChange={(e) => setQuantity(e.currentTarget.value)}
        />
      </div>
      <Field
        label="Why"
        hint="Required. It stays with the change, e.g. “Two found cut at the count”."
        required
        value={reason}
        error={askWhy && !reason.trim() ? 'Say why.' : undefined}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
      <p className="text-xs text-muted">
        Removing stock that jobs hold is allowed — it was lost, after all — and those jobs then show
        short.
      </p>
    </FormShell>
  );
}
